import React from 'react';
import type { PaginationMeta } from '@yawi/domain';
import { PAGE_SIZE_OPTIONS } from '@yawi/domain';

interface PaginationProps {
  meta: PaginationMeta | null;
  onPageChange: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  loading?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  meta,
  onPageChange,
  pageSize,
  onPageSizeChange,
  loading = false
}) => {
  // Show pagination even with single page if we have page size selector
  if (!meta || (meta.totalPages <= 1 && !onPageSizeChange)) return null;

  const { page, total, totalPages, hasNextPage, hasPreviousPage, limit } = meta;

  // Un filtro que no deja resultados da total=0: mostrar "1 a 0" sería un
  // rango imposible, así que se oculta el rango y se muestra solo el total.
  const startRecord = total === 0 ? 0 : (page - 1) * limit + 1;
  const endRecord = total === 0 ? 0 : Math.min(page * limit, total);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (page > 3) {
        pages.push('ellipsis');
      }

      // Show pages around current page
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('ellipsis');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const buttonBaseClass = `
    inline-flex items-center justify-center w-9 h-9 text-sm font-medium rounded-lg
    transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const pageButtonClass = (isActive: boolean) => `
    ${buttonBaseClass}
    ${isActive
      ? 'bg-primary-600 text-white hover:bg-primary-700'
      : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
    }
  `;

  const navButtonClass = `
    ${buttonBaseClass}
    bg-white text-slate-700 border border-slate-200 hover:bg-slate-50
  `;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-white border-t border-slate-100">
      {/* Left side: Info text and page size selector */}
      <div className="flex items-center gap-4">
        <div className="text-sm text-slate-500">
          {total === 0 ? (
            <>Mostrando <span className="font-medium text-slate-800">0</span> registros</>
          ) : (
            <>
              Mostrando <span className="font-medium text-slate-800">{startRecord}</span> a{' '}
              <span className="font-medium text-slate-800">{endRecord}</span> de{' '}
              <span className="font-medium text-slate-800">{total}</span> registros
            </>
          )}
        </div>

        {/* Page size selector */}
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Mostrar:</span>
            <select
              value={pageSize || limit}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              disabled={loading}
              className="px-2 py-1 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:opacity-50"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-2">
        {/* Previous button */}
        <button
          className={navButtonClass}
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPreviousPage || loading}
          aria-label="Página anterior"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Page numbers */}
        <div className="hidden sm:flex items-center gap-1">
          {getPageNumbers().map((pageNum, index) =>
            pageNum === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="px-2 text-slate-400">
                ...
              </span>
            ) : (
              <button
                key={pageNum}
                className={pageButtonClass(pageNum === page)}
                onClick={() => onPageChange(pageNum)}
                disabled={loading}
                aria-current={pageNum === page ? 'page' : undefined}
              >
                {pageNum}
              </button>
            )
          )}
        </div>

        {/* Mobile page indicator */}
        <span className="sm:hidden text-sm text-slate-500">
          {page} / {totalPages}
        </span>

        {/* Next button */}
        <button
          className={navButtonClass}
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNextPage || loading}
          aria-label="Página siguiente"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};
